import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, Input, Callout, Icon } from '@node-space/storybook-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { PostLoginReq } from 'types/authentication/PostLoginReq'
import { ErrorData, WorkspaceListItemId } from 'types/types'
import { HeaderCTA } from 'components/header/HeaderCTA'
import LoginPageLayout from 'components/layout/LoginPageLayout'
import { useLoginMutation } from 'hooks/mutations/useLoginMutation'
import { isRecapthcaLowScoreError } from 'utils/recaptcha'
import { RecaptchaProps } from '../LoginDeveloper'
import { RecaptchaTokenError } from 'components/recaptcha/RecaptchaTokenError'
import { RecaptchaScoreError } from 'components/recaptcha/RecaptchaScoreError'

interface LoginFormProps {
  setSuccessfulLogin: () => Promise<void>
  recaptcha: RecaptchaProps
}

const LoginForm = ({ setSuccessfulLogin, recaptcha }: LoginFormProps) => {
  const { t } = useTranslation()

  const [errorMessage, setErrorMessage] = useState<React.ReactNode>()
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)

  const { mutate } = useLoginMutation()
  const { isLoading: isProfileLoading } = useProfileContext()

  const { register, handleSubmit } = useForm<PostLoginReq>({
    defaultValues: {
      emailAddress: '',
      password: '',
      recaptcha: '',
    },
  })

  const isLoading = useMemo(
    () => isProfileLoading || recaptcha?.isLoading || isLoadingSubmit,
    [isProfileLoading, recaptcha?.isLoading, isLoadingSubmit]
  )

  const handleLogin = async (values: PostLoginReq) => {
    setErrorMessage(null)
    setIsLoadingSubmit(true)
    const recaptchaToken = await recaptcha?.getToken()

    if (!recaptchaToken) {
      setErrorMessage(<RecaptchaTokenError />)
      setIsLoadingSubmit(false)
      return
    }

    const payload: PostLoginReq = {
      ...values,
      recaptcha: recaptchaToken,
      recaptchaVersion: recaptcha?.version,
    }

    mutate(payload, {
      onSuccess: async () => {
        await setSuccessfulLogin()
      },
      onError: async (error: {
        status: number
        data: [WorkspaceListItemId] & ErrorData
        message?: string
      }) => {
        if (error.status === 406 || error.status === 409) {
          setErrorMessage(t('unverifiedAccountsOnly'))
        } else if (isRecapthcaLowScoreError(error?.data?.message)) {
          setErrorMessage(<RecaptchaScoreError />)
        } else {
          setErrorMessage(`${!!error?.status && `${error?.status} - `}${t('loginError')}`)
        }
      },
      onSettled: async () => {
        setIsLoadingSubmit(false)
      },
    })
  }

  return (
    <LoginPageLayout
      title={t('logInYourAccount')}
      headerRightContent={
        <HeaderCTA
          helpText={`${t('havingAccountQuestion')}`}
          buttonText={`${t('signUp')}`}
          buttonUrl="/create-account"
        />
      }
      hasFooter
    >
      {errorMessage && <Callout state="error">{errorMessage}</Callout>}
      <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
        <div className="space-y-5">
          <Input
            type="email"
            inputMode="email"
            placeholder={t('yourEmailAddress')}
            label={t('email')}
            testid="emailAddress"
            {...register('emailAddress')}
          />
          <Input
            placeholder={t('enterPassword')}
            label={t('password')}
            rightLabel={
              <p className="text-sm text-secondary-500 font-medium hover:underline">
                <Link to={`${process.env.MERCHANT_PORTAL_URL}/forgot-password`}>
                  {t('forgotPassword')}
                </Link>
              </p>
            }
            type="password"
            testid="password"
            {...register('password')}
          />
        </div>
        <Button
          type="submit"
          loading={isLoading}
          full
          testid="loginButton"
          iconElement={<Icon name="LockIcon" color="inherit" />}
        >
          {`${t('logIn')}`}
        </Button>
      </form>
    </LoginPageLayout>
  )
}

export default LoginForm
